import { protectorApiClient, type RequestProps } from '@protector/api-client';
import type { CharacteristicQueryDTO, MethodologySeasonField, MethodologySeasonFieldsPage, PhenomenonSingle } from './models.ts';
import { methodologySeasonFieldsPageSchema } from './schemas.ts';

function requestWithDomain<I, O>(args: Omit<RequestProps<I, O>, 'domain'>) {
  return protectorApiClient.request<I, O>({ ...args, domain: 'Methodologies' });
}

export async function getMethodologySeasonFields(propertyId: string, seasonIds: string[], fieldIds: string[]): Promise<MethodologySeasonField[]> {
  const seenKeys = new Set<string>();

  async function getPage(currentKey?: string): Promise<MethodologySeasonField[]> {
    const response = await requestWithDomain<unknown, MethodologySeasonFieldsPage>({
      config: {
        method: 'POST',
        url: '/v1/methodologies/season-fields/list',
        data: { property_ids: [propertyId], season_ids: seasonIds, field_ids: fieldIds },
        params: currentKey ? new URLSearchParams({ current_key: currentKey }) : undefined
      }
    });
    const page = methodologySeasonFieldsPageSchema.parse(response.data);
    const nextKey = page.pageable.next_key;

    if (!nextKey) return page.content;
    if (seenKeys.has(nextKey)) throw new Error('Repeated methodology season fields pagination cursor');
    seenKeys.add(nextKey);
    return page.content.concat(await getPage(nextKey));
  }

  return getPage();
}

export async function getMethodologyIdsForSeasonFields(propertyId: string, seasonIds: string[], fieldIds: string[]): Promise<string[]> {
  const seasonFields = await getMethodologySeasonFields(propertyId, seasonIds, fieldIds);
  const methodologyIds = seasonFields.map(seasonField => seasonField.methodology_id).filter(methodologyId => methodologyId !== null);

  return [...new Set(methodologyIds)];
}

export function getCharacteristicsByMethodologyId(methodologyId: string): Promise<CharacteristicQueryDTO[]> {
  return requestWithDomain<unknown, CharacteristicQueryDTO[]>({
    config: {
      method: 'GET',
      url: `/api/v1/methodologies/${methodologyId}/characteristics`
    }
  }).then(response => response.data);
}

export function getPhenomenaByMethodologyId(methodologyId: string): Promise<PhenomenonSingle[]> {
  return requestWithDomain<unknown, PhenomenonSingle[]>({
    config: {
      method: 'GET',
      url: `/api/v1/methodologies/${methodologyId}/phenomenon`
    }
  }).then(response => response.data);
}
